import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const AyurvedaButton = React.forwardRef(({ 
  className, 
  variant = 'primary',
  size = 'default',
  animate = true,
  asChild = false,
  children,
  disabled,
  ...props 
}, ref) => {
  const variantStyles = {
    primary: 'bg-gradient-to-r from-ayurveda-sage-500 to-ayurveda-herbal-500 text-white shadow-lg hover:shadow-xl',
    secondary: 'glass-card text-ayurveda-sage-700 border-ayurveda-sage-300 hover:bg-ayurveda-sage-100',
    outline: 'border-2 border-ayurveda-sage-500 text-ayurveda-sage-700 bg-transparent hover:bg-ayurveda-sage-500 hover:text-white',
    ghost: 'text-ayurveda-sage-700 hover:bg-ayurveda-sage-100',
    wellness: 'bg-gradient-to-r from-ayurveda-gold-500 to-ayurveda-sandalwood-500 text-white shadow-lg hover:shadow-xl',
  };

  const sizes = {
    sm: 'h-9 px-3 text-sm',
    default: 'h-11 px-6 py-2',
    lg: 'h-12 px-8 text-lg',
    xl: 'h-14 px-10 text-xl',
  };

  const baseClasses = cn(
    'inline-flex items-center justify-center rounded-xl font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ayurveda-sage-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variantStyles[variant],
    sizes[size],
    disabled && 'cursor-not-allowed opacity-50',
    className
  );

  // If asChild, use Slot without motion
  if (asChild) {
    return (
      <Slot ref={ref} className={baseClasses} {...props}>
        {children}
      </Slot>
    );
  }

  // If animate is false, use regular button
  if (!animate) {
    return (
      <button
        ref={ref}
        className={baseClasses}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }

  // Animated button
  return (
    <motion.button
      ref={ref}
      className={baseClasses}
      disabled={disabled}
      whileHover={{ 
        scale: disabled ? 1 : 1.05,
        transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] }
      }}
      whileTap={{ 
        scale: disabled ? 1 : 0.95,
        transition: { duration: 0.1 }
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      {...props}
    >
      {children}
    </motion.button>
  );
});

AyurvedaButton.displayName = 'AyurvedaButton';

export { AyurvedaButton };